import * as RadixPopover from '@radix-ui/react-popover';
import {
  Fragment,
  forwardRef,
  type MouseEvent,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type WorkspaceAgentContextAddedMethod } from '@air/analytics';
import { ClipType } from '@air/api/types';
import { AtSignIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { usePortalStore } from '@air/primitive-portal';
import { Spinner } from '@air/primitive-spinner';
import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentAssetMentionRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentAssetMentionRow';
import { WorkspaceAgentBoardTreeRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentBoardTreeRow';
import { WorkspaceAgentBrandKitMentionRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentBrandKitMentionRow';
import { WorkspaceAgentMentionContextsBody } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionContextsBody';
import { WorkspaceAgentMentionFilterChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionFilterChip';
import { WorkspaceAgentMentionImageRowPreview } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionImageRowPreview';
import { WorkspaceAgentMentionListSentinel } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionListSentinel';
import { WorkspaceAgentMentionNodeSentinel } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionNodeSentinel';
import {
  COMPOSER_PICKER_BOTTOM_SIDE_OFFSET_PX,
  type WorkspaceAgentComposerPopoverAnchorRef,
} from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/utils/composerPopoverAnchor';
import {
  WORKSPACE_AGENT_COMPOSER_REFERENCE_MENU_POPOVER_HEIGHT_PX,
  WORKSPACE_AGENT_REFERENCE_MENU_TITLE,
  workspaceAgentReferenceMenuHeader,
  workspaceAgentReferenceMenuPopover,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerPopoverChrome';
import { WORKSPACE_AGENT_MENTION_LIST_RING_CLEARANCE_STYLE } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentMentionRowChrome';
import { PrivateWorkspaceAgentBoardListQuery } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/PrivateWorkspaceAgentBoardListQuery';
import {
  type WorkspaceAgentBrandKitOption,
  type WorkspaceAgentMentionableItem,
  type WorkspaceAgentMentionItem,
  type WorkspaceAgentMentionRowItem,
  type WorkspaceAgentMentionSource,
  type WorkspaceAgentMentionSourceKey,
  type WorkspaceAgentMentionTreeItem,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { useWorkspaceAgentMentionChildPrefetch } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/useWorkspaceAgentMentionChildPrefetch';
import { useWorkspaceAgentMentionExpansionAnnouncement } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/useWorkspaceAgentMentionExpansionAnnouncement';
import { useWorkspaceAgentMentionRowPreview } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/useWorkspaceAgentMentionRowPreview';
import {
  type WorkspaceAgentBoardListQuerySpec,
  type WorkspaceAgentBoardListSnapshotHandler,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentBoardListQuery';
import {
  WORKSPACE_AGENT_MENTION_LIST_ID,
  type WorkspaceAgentMentionDismissReason,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionConstants';
import {
  countWorkspaceAgentMentionHits,
  getFirstWorkspaceAgentMentionableIndex,
  getFirstWorkspaceAgentMentionHitIndex,
  isWorkspaceAgentMentionableItem,
  isWorkspaceAgentMentionTreeItem,
  splitWorkspaceAgentMentionLibraryRun,
  toWorkspaceAgentMentionSentinelEndIndexes,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionItems';

/** Radix's own outside-press event, taken off the prop it is handed to rather than re-described. */
type WorkspaceAgentMentionPointerDownOutsideEvent = Parameters<
  NonNullable<RadixPopover.PopoverContentProps['onPointerDownOutside']>
>[0];

const WORKSPACE_AGENT_MENTION_OPTION_ID_PREFIX = 'workspace-agent-mention-option-';

const getMentionOptionId = (item: WorkspaceAgentMentionItem) => `${WORKSPACE_AGENT_MENTION_OPTION_ID_PREFIX}${item.id}`;

/** Keyboard nav's `scrollIntoView` leaves the sentinel just below the fold — prefetch before that. */
const MENTION_SENTINEL_ROOT_MARGIN = '0px 0px 48px 0px';

/** Names both the visual heading and the group the library rows sit in. */
const WORKSPACE_AGENT_MENTION_LIBRARIES_LABEL = 'Libraries';

export type { WorkspaceAgentMentionDismissReason };

export type WorkspaceAgentMentionPopupRef = {
  /** Returns `true` when the popup consumed the key, so the composer can skip Enter-to-send. */
  onKeyDown: (event: KeyboardEvent) => boolean;
};

export type WorkspaceAgentMentionPopupProps = {
  activeSourceKey: WorkspaceAgentMentionSourceKey;
  /** Every kit in the workspace. The Contexts tab offers an inline selector once there are two. */
  brandKits: readonly WorkspaceAgentBrandKitOption[];
  /**
   * The composer's own box. The picker sits flush on its top edge and takes its width, so it reads
   * as the composer growing upwards rather than as a menu hung off the caret.
   */
  anchorRef: WorkspaceAgentComposerPopoverAnchorRef;
  hasNextPage: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isError: boolean;
  /** Rows belong to an older query: shown as pending, and not pickable. */
  isStale: boolean;
  items: WorkspaceAgentMentionRowItem[];
  /** The listbox never holds focus, so the highlighted row's id goes to the editor's `aria-activedescendant`. */
  onActiveOptionChange: (optionId: string | null) => void;
  onDismiss: (reason: WorkspaceAgentMentionDismissReason) => void;
  onFetchNextPage: () => void;
  onFetchNextPageForNode: (nodeId: string) => void;
  onRetry: () => void;
  /** The kit menu is the one control that takes focus, so closing it has to give the caret back. */
  onRefocusEditor: () => void;
  onSelect: (item: WorkspaceAgentMentionableItem, method: WorkspaceAgentContextAddedMethod) => void;
  onSelectBrandKit: (brandKitId: string) => void;
  onSourceChange: (sourceKey: WorkspaceAgentMentionSourceKey) => void;
  onToggleExpanded: ((nodeId: string, isExpanded: boolean) => void) | undefined;
  /**
   * Tree sources only: rows whose own children are in flight or came back failed, so what they hold
   * cannot be counted yet. `isFetching` is the whole source and says nothing about which row.
   */
  pendingChildCountNodeIds: ReadonlySet<string> | undefined;
  hasNextPageNodeIds: ReadonlySet<string>;
  listQuerySpecs: WorkspaceAgentBoardListQuerySpec[];
  onListSnapshot: WorkspaceAgentBoardListSnapshotHandler;
  query: string;
  selectedBrandKitId: string | null;
  workspaceId: string | undefined;
  /** Radix treats the composer's `@` button and the editor as "outside"; presses there must not dismiss. */
  shouldIgnoreOutsidePress: (target: Node) => boolean;
  side?: 'top' | 'bottom';
  sideOffset?: number;
  sources: readonly WorkspaceAgentMentionSource[];
};

/** Everything the suggestion session knows; the composer adds its own box as the anchor. */
export type WorkspaceAgentMentionPopupSessionProps = Omit<WorkspaceAgentMentionPopupProps, 'anchorRef'>;

/**
 * Presentation of the `@` picker; owns the highlighted row and whether its preview is up. Swallows
 * every key it navigates with — including Enter on an empty list, which must not fall through to
 * Enter-to-send.
 */
export const WorkspaceAgentMentionPopup = memo(
  forwardRef<WorkspaceAgentMentionPopupRef, WorkspaceAgentMentionPopupProps>((props, ref) => {
    const {
      activeSourceKey,
      anchorRef,
      brandKits,
      hasNextPage,
      hasNextPageNodeIds,
      isError,
      isFetching,
      isLoading,
      isStale,
      items,
      listQuerySpecs,
      onDismiss,
      onFetchNextPageForNode,
      onListSnapshot,
      onRefocusEditor,
      onRetry,
      onSelect,
      onSelectBrandKit,
      onSourceChange,
      onToggleExpanded,
      pendingChildCountNodeIds,
      query,
      selectedBrandKitId,
      shouldIgnoreOutsidePress,
      side = 'top',
      sideOffset,
      sources,
      workspaceId,
    } = props;

    const resolvedSideOffset = sideOffset ?? COMPOSER_PICKER_BOTTOM_SIDE_OFFSET_PX;

    const [selectedIndex, setSelectedIndex] = useState(0);
    /**
     * Which input last moved the highlight. The fill is the same either way — it is where the pointer
     * would have put it — so only the keyboard's highlight is ringed, and only until the pointer moves
     * again. Nothing else on screen says where `aria-activedescendant` points while the editor holds focus.
     */
    const [isKeyboardHighlight, setIsKeyboardHighlight] = useState(false);
    const [isContextsKitMenuOpen, setIsContextsKitMenuOpen] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);
    /** The Contexts kit dropdown trigger, which Shift+Tab hands focus to while that tab is active. */
    const contextsKitTriggerRef = useRef<HTMLButtonElement>(null);
    const [mentionPreviewAnchorElement, setMentionPreviewAnchorElement] = useState<HTMLElement | null>(null);
    const portal = usePortalStore((state) => state.portal);

    const registerMentionPreviewAnchorElement = useCallback((element: HTMLElement | null) => {
      setMentionPreviewAnchorElement((current) => (current === element ? current : element));
    }, []);

    const getMentionPreviewAnchorElement = useCallback(() => {
      const element = mentionPreviewAnchorElement;

      if (!element?.isConnected) {
        return null;
      }

      const rect = element.getBoundingClientRect();

      if (rect.width <= 0 && rect.height <= 0) {
        return null;
      }

      return element;
    }, [mentionPreviewAnchorElement]);

    // Read by the key handler, the sentinel observer and the anchor — none of which rebind per render.
    const latestRef = useRef(props);
    latestRef.current = props;

    // Clamped in render, not an effect, so no frame paints an out-of-range index.
    const highlightedIndex = items.length > 0 ? Math.min(selectedIndex, items.length - 1) : 0;
    const { prefetchSpec } = useWorkspaceAgentMentionChildPrefetch({
      highlightedIndex,
      items,
      listQuerySpecs,
      onToggleExpanded,
      query,
      workspaceId,
    });
    // Same `key` as expand so a highlight that becomes an open reuses the observer instead of
    // remounting it (staleTime 0 would refetch).
    const boardListQuerySpecs =
      prefetchSpec && listQuerySpecs.every((spec) => spec.nodeId !== prefetchSpec.nodeId)
        ? [...listQuerySpecs, prefetchSpec]
        : listQuerySpecs;

    // Side effects read the ref: a `useState` updater may run twice, and `onSelect` inside one would mint two pills.
    const highlightedIndexRef = useRef(highlightedIndex);
    highlightedIndexRef.current = highlightedIndex;

    const previousHighlightedItemIdRef = useRef<string>();
    const previousHighlightQueryRef = useRef(query);

    /**
     * Only this row's own children, never the source-wide fetch: a library heading and a typed
     * query would otherwise mark every row busy.
     */
    const hasPendingChildCount = useCallback(
      (item: WorkspaceAgentMentionTreeItem) => pendingChildCountNodeIds?.has(item.id) ?? false,
      [pendingChildCountNodeIds],
    );

    const { expansionMessage } = useWorkspaceAgentMentionExpansionAnnouncement({
      activeSourceKey,
      hasPendingChildCount,
      items,
      query,
    });

    useEffect(() => {
      // Follow the id, not the index: a fetched page or an opened board keeps the row the user was on.
      // Highlight handlers write the ref first so a same-tick list rebuild cannot snap back to the
      // first mentionable and scrollIntoView under the pointer. A new query starts on the first hit
      // even when that hit's ancestor is the row browse had highlighted.
      const queryChanged = previousHighlightQueryRef.current !== query;
      previousHighlightQueryRef.current = query;

      if (queryChanged) {
        previousHighlightedItemIdRef.current = undefined;
      }

      const previousId = previousHighlightedItemIdRef.current;
      const nextIndex = previousId ? items.findIndex((item) => item.id === previousId) : -1;

      // Index 0 is often Global now; a heading cannot be mentioned. Under a query, skip ancestors
      // that only place a hit, so the highlight lands on the first hit.
      setSelectedIndex((current) => {
        const fallbackIndex =
          query === '' ? getFirstWorkspaceAgentMentionableIndex(items) : getFirstWorkspaceAgentMentionHitIndex(items);
        const resolved = nextIndex === -1 ? fallbackIndex : nextIndex;

        return resolved === current ? current : resolved;
      });
    }, [items, query]);

    const handleFetchNextPage = useCallback(() => {
      latestRef.current.onFetchNextPage();
    }, []);

    const sentinelEndIndexes = useMemo(
      () => toWorkspaceAgentMentionSentinelEndIndexes(items, hasNextPageNodeIds),
      [hasNextPageNodeIds, items],
    );

    const highlightedItem = items[highlightedIndex];
    const activeOptionId = isContextsKitMenuOpen || !highlightedItem ? null : getMentionOptionId(highlightedItem);
    /**
     * Image rows with a still — an asset or a brand kit image. The preview *is* the still, so a row
     * without one has nothing to show, and a board row or a library heading is not what this picker
     * previews at all.
     */
    const previewItem =
      (highlightedItem?.kind === 'asset' || highlightedItem?.kind === 'contextSourceFieldValue') &&
      highlightedItem.thumbnailUrl
        ? highlightedItem
        : undefined;

    const previewScopeKey = useMemo(
      () => `${activeSourceKey}:${selectedBrandKitId ?? ''}`,
      [activeSourceKey, selectedBrandKitId],
    );

    const { armRowPreview, closeRowPreview, isRowPreviewArmed, isRowPreviewOpen } = useWorkspaceAgentMentionRowPreview({
      itemId: previewItem?.id,
      previewResetScopeKey: previewScopeKey,
      query,
    });

    /**
     * Escape closes the contexts kit menu first when it is open; otherwise it dismisses the picker.
     * The row preview is decorative (`aria-hidden`) — closing it alone would read as a press that did
     * nothing. While the preview is painted it is Radix's topmost layer, so this handler is also wired
     * on the preview Content; without that, capture-phase Escape never reaches the editor or the
     * picker's own `onEscapeKeyDown`.
     */
    const handleEscapeKeyDown = useCallback(
      (event: KeyboardEvent) => {
        event.preventDefault();

        if (isContextsKitMenuOpen) {
          setIsContextsKitMenuOpen(false);
          onRefocusEditor();

          return;
        }

        onDismiss('escape');
      },
      [isContextsKitMenuOpen, onDismiss, onRefocusEditor],
    );

    // Every commit, after the effect above reads it. Skip stale rows: they still contain the
    // browse/previous-query id, and writing it here would pin the highlight to an ancestor once the
    // matching tree lands.
    useEffect(() => {
      if (isStale) {
        return;
      }

      previousHighlightedItemIdRef.current = highlightedItem?.id;
    });

    useEffect(() => {
      latestRef.current.onActiveOptionChange(activeOptionId);
    }, [activeOptionId]);

    useEffect(() => () => latestRef.current.onActiveOptionChange(null), []);

    const selectItem = useCallback(
      (item: WorkspaceAgentMentionItem | undefined, method: WorkspaceAgentContextAddedMethod) => {
        if (latestRef.current.isStale || !item) {
          return;
        }

        // A library heads a section; there is no library pill, so picking one only opens it.
        if (item.kind === 'library') {
          latestRef.current.onToggleExpanded?.(item.id, item.isExpanded);

          return;
        }

        onSelect(item, method);
      },
      [onSelect],
    );

    /** A pick is resolved against the rows that exist *now*, so a refetch mid-click can't swap it. */
    const selectItemById = useCallback(
      (itemId: string) => {
        selectItem(
          latestRef.current.items.find((item) => item.id === itemId),
          'button',
        );
      },
      [selectItem],
    );

    /** A row taken by the pointer is not the keyboard's, so the ring comes off with the move. */
    const handleRowHighlight = useCallback((index: number) => {
      setIsKeyboardHighlight(false);
      previousHighlightedItemIdRef.current = latestRef.current.items[index]?.id;
      setSelectedIndex(index);
    }, []);

    /** Only image rows arm a preview, and `onHighlight` is the pointer: hovering one is the ask for it. */
    const handleImageRowHighlight = useCallback(
      (index: number) => {
        armRowPreview();
        handleRowHighlight(index);
      },
      [armRowPreview, handleRowHighlight],
    );

    /** The pointer is driving again even if it never reached a row — a scroll can move one under it. */
    const handleListMouseMove = useCallback(() => {
      setIsKeyboardHighlight(false);
    }, []);

    /** A mousedown on the retry button would blur the editor. */
    const handleRetryMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    }, []);

    const handleContextsKitMenuOpening = useCallback(() => {
      closeRowPreview();
      latestRef.current.onActiveOptionChange(null);
    }, [closeRowPreview]);

    const handleSelectBrandKit = useCallback(
      (brandKitId: string) => {
        closeRowPreview();
        setIsContextsKitMenuOpen(false);
        onSelectBrandKit(brandKitId);
      },
      [closeRowPreview, onSelectBrandKit],
    );

    useEffect(() => {
      if (activeSourceKey !== 'brand-kit') {
        setIsContextsKitMenuOpen(false);
      }
    }, [activeSourceKey]);

    /**
     * Shift+Tab off the caret steps back past the picker. The caret lives in a portal, so the browser
     * would continue from the portal's place in the DOM rather than from the composer: focus is
     * handed to the editor instead, and the picker closes with it, the same rule the `'tab'` path
     * below keeps — the picker is never left standing over whatever takes focus next. Closing is also
     * what stops the caret being a trap: the next Shift+Tab is the editor's and leaves the composer.
     */
    /** The editor keeps focus while the picker is open. */
    const handleOpenAutoFocus = useCallback((event: Event) => {
      event.preventDefault();
    }, []);

    const handlePointerDownOutside = useCallback(
      (event: WorkspaceAgentMentionPointerDownOutsideEvent) => {
        const target = event.detail.originalEvent.target;

        if (target instanceof Node && shouldIgnoreOutsidePress(target)) {
          return;
        }

        onDismiss('pointer-outside');
      },
      [onDismiss, shouldIgnoreOutsidePress],
    );

    useImperativeHandle(
      ref,
      () => ({
        onKeyDown: (event: KeyboardEvent) => {
          const { isError: hasSearchError, items: currentItems, onRetry: retry } = latestRef.current;
          const optionCount = currentItems.length;

          // The kit menu must own the keyboard while it is open, so this is the one key that moves
          // focus out of the composer — and only on the tab whose chip actually has a selector.
          if (
            event.key === 'Tab' &&
            event.shiftKey &&
            !event.metaKey &&
            !event.ctrlKey &&
            !event.altKey &&
            latestRef.current.activeSourceKey === 'brand-kit' &&
            latestRef.current.brandKits.length > 1 &&
            contextsKitTriggerRef.current
          ) {
            contextsKitTriggerRef.current.focus();

            return true;
          }

          // Tab is the user leaving the composer, not picking a row. Plain Tab with rows picks one
          // (below); with none it belongs to the browser's focus order, and Shift+Tab always does —
          // it steps backwards, past the composer. Either way the picker must not be left standing
          // over whatever takes focus next.
          if (
            event.key === 'Tab' &&
            (optionCount === 0 || event.shiftKey) &&
            !event.metaKey &&
            !event.ctrlKey &&
            !event.altKey
          ) {
            onDismiss('tab');

            return false;
          }

          // The suggestion plugin routes keys here too, so the chord guard lives on this side as
          // well: Shift+Enter is a newline, not a pick, while the picker is open.
          if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) {
            return false;
          }

          // Rows are unmounted while the kit menu is open; swallow list navigation so highlight does not move behind it.
          if (
            isContextsKitMenuOpen &&
            (event.key === 'Enter' ||
              event.key === ' ' ||
              event.key === 'ArrowDown' ||
              event.key === 'ArrowUp' ||
              event.key === 'Home' ||
              event.key === 'End')
          ) {
            return true;
          }

          if (event.key === 'Escape') {
            if (isContextsKitMenuOpen) {
              setIsContextsKitMenuOpen(false);
              onRefocusEditor();

              return true;
            }

            onDismiss('escape');

            return true;
          }

          // Browse-tree Left/Right stay on `@` even when the row cannot toggle (leaf / already open).
          // Typed query and listbox sources still hand the caret to ProseMirror.
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            const { activeSourceKey, onToggleExpanded, query, sources } = latestRef.current;
            const listRole = sources.find((source) => source.key === activeSourceKey)?.listRole;
            const item = currentItems[highlightedIndexRef.current];
            const shouldExpand = event.key === 'ArrowRight';

            if (query !== '' || listRole !== 'tree' || !onToggleExpanded) {
              return false;
            }

            if (item && isWorkspaceAgentMentionTreeItem(item) && item.hasChildren && item.isExpanded !== shouldExpand) {
              onToggleExpanded(item.id, item.isExpanded);
            }

            return true;
          }

          // A library heading cannot be picked, so Home and End land on the first and last row that
          // can — only the empty-query tree, same as Left/Right. Assets recents leave them to the editor.
          if (event.key === 'Home' || event.key === 'End') {
            const { activeSourceKey, query, sources } = latestRef.current;
            const listRole = sources.find((source) => source.key === activeSourceKey)?.listRole;

            if (query !== '' || listRole !== 'tree') {
              return false;
            }

            const selectableIndexes = currentItems.flatMap((item, index) =>
              isWorkspaceAgentMentionableItem(item) ? [index] : [],
            );
            const nextIndex = event.key === 'Home' ? selectableIndexes[0] : selectableIndexes.at(-1);

            if (nextIndex === undefined) {
              return false;
            }

            setIsKeyboardHighlight(true);
            previousHighlightedItemIdRef.current = currentItems[nextIndex]?.id;
            armRowPreview();
            setSelectedIndex(nextIndex);

            return true;
          }

          // With no rows there is nothing to move between, so the arrows stay the editor's: caret
          // movement is the only thing left for them to do.
          if (event.key === 'ArrowDown') {
            if (optionCount > 0) {
              const nextIndex = (highlightedIndexRef.current + 1) % optionCount;

              setIsKeyboardHighlight(true);
              previousHighlightedItemIdRef.current = currentItems[nextIndex]?.id;
              armRowPreview();
              setSelectedIndex(nextIndex);
            }

            return optionCount > 0;
          }

          if (event.key === 'ArrowUp') {
            if (optionCount > 0) {
              const nextIndex = (highlightedIndexRef.current - 1 + optionCount) % optionCount;

              setIsKeyboardHighlight(true);
              previousHighlightedItemIdRef.current = currentItems[nextIndex]?.id;
              armRowPreview();
              setSelectedIndex(nextIndex);
            }

            return optionCount > 0;
          }

          if (event.key === 'Enter' || event.key === 'Tab') {
            // The Retry button is not a tab stop — focus never leaves the editor — so Enter on a
            // failed source is the only way a keyboard user can reach it. The live region
            // announces the retry as it announced the failure.
            if (event.key === 'Enter' && hasSearchError && optionCount === 0) {
              retry();

              return true;
            }

            const highlightedItem = currentItems[highlightedIndexRef.current];

            // A library heading cannot be mentioned. Tab must leave the composer, not toggle + trap.
            if (event.key === 'Tab' && highlightedItem?.kind === 'library') {
              onDismiss('tab');

              return false;
            }

            selectItem(highlightedItem, 'keyboard');

            // Swallowed even with nothing to pick, or Enter-to-send would post the bare `@query`.
            return true;
          }

          return false;
        },
      }),
      [armRowPreview, isContextsKitMenuOpen, onDismiss, onRefocusEditor, selectItem],
    );

    /** Both runs render the same row; only the index differs, and it is the list's, not the run's. */
    const renderRow = useCallback(
      (item: WorkspaceAgentMentionRowItem, index: number) => {
        if (isWorkspaceAgentMentionTreeItem(item)) {
          return (
            <WorkspaceAgentBoardTreeRow
              id={getMentionOptionId(item)}
              index={index}
              isKeyboardHighlighted={isKeyboardHighlight && index === highlightedIndex}
              isPendingChildCount={hasPendingChildCount(item)}
              isSelected={index === highlightedIndex}
              item={item}
              key={item.id}
              onHighlight={handleRowHighlight}
              onSelect={selectItemById}
              onToggleExpanded={onToggleExpanded}
            />
          );
        }

        if (item.kind === 'contextSourceFieldValue') {
          return (
            <WorkspaceAgentBrandKitMentionRow
              id={getMentionOptionId(item)}
              index={index}
              isKeyboardHighlighted={isKeyboardHighlight && index === highlightedIndex}
              isSelected={index === highlightedIndex}
              item={item}
              key={item.id}
              onHighlight={handleImageRowHighlight}
              onPreviewAnchorElementChange={registerMentionPreviewAnchorElement}
              onSelect={selectItemById}
            />
          );
        }

        return (
          <WorkspaceAgentAssetMentionRow
            id={getMentionOptionId(item)}
            index={index}
            isKeyboardHighlighted={isKeyboardHighlight && index === highlightedIndex}
            isSelected={index === highlightedIndex}
            item={item}
            key={item.id}
            onHighlight={handleImageRowHighlight}
            onPreviewAnchorElementChange={registerMentionPreviewAnchorElement}
            onSelect={selectItemById}
          />
        );
      },
      [
        handleImageRowHighlight,
        handleRowHighlight,
        hasPendingChildCount,
        highlightedIndex,
        isKeyboardHighlight,
        onToggleExpanded,
        registerMentionPreviewAnchorElement,
        selectItemById,
      ],
    );

    const renderRowWithSentinels = useCallback(
      (item: WorkspaceAgentMentionRowItem, index: number) => {
        const sentinelNodeIds = sentinelEndIndexes.get(index);

        return (
          <Fragment key={item.id}>
            {renderRow(item, index)}
            {sentinelNodeIds?.map((nodeId) => (
              <WorkspaceAgentMentionNodeSentinel
                isFetching={false}
                key={nodeId}
                listRef={listRef}
                nodeId={nodeId}
                onFetchNextPageForNode={onFetchNextPageForNode}
                rootMargin={MENTION_SENTINEL_ROOT_MARGIN}
              />
            ))}
          </Fragment>
        );
      },
      [onFetchNextPageForNode, renderRow, sentinelEndIndexes],
    );

    const activeSource = sources.find((source) => source.key === activeSourceKey);

    // Every key the picker can be on is a registry entry, so this is unreachable — but resolving it
    // once here is what lets the copy below read straight off the source with no fallbacks.
    if (!activeSource) {
      return null;
    }

    // Hits for the live region. Empty/loading keys off rows so a Libraries heading with no boards
    // yet still draws the tree instead of the empty state.
    const resultCount = countWorkspaceAgentMentionHits(items);
    const hasRows = items.length > 0;
    const hasFailed = isError && !hasRows;
    const isResolving = !hasRows && !hasFailed && (isLoading || isFetching || isStale);
    const showEmptyState = !hasRows && !isResolving;
    let emptyStateTitle = activeSource.emptyStateMessage;

    if (hasFailed) {
      emptyStateTitle = activeSource.errorStateMessage;
    } else if (query === '') {
      // An empty query lists recents, so zero rows means the source is empty.
      emptyStateTitle = activeSource.noItemsMessage;
    }
    let liveMessage = '';

    if (isResolving) {
      liveMessage = 'Loading';
    } else if (expansionMessage) {
      liveMessage = expansionMessage;
    } else if (resultCount > 0) {
      // Source name included so switching sources is announced too.
      liveMessage = `${activeSource.label}, ${resultCount === 1 ? '1 result' : `${resultCount} results`}`;
    } else if (hasRows) {
      // Collapsed library headings are still rows, not an empty source.
      liveMessage = activeSource.label;
    } else {
      liveMessage = emptyStateTitle;
    }

    const { firstLibraryIndex, leadingItems, libraryRunItems } = splitWorkspaceAgentMentionLibraryRun(items);
    const headerStyles = workspaceAgentReferenceMenuHeader();
    const popoverStyles = workspaceAgentReferenceMenuPopover();
    const isContextsSource = activeSourceKey === 'brand-kit';
    /** Icon slot only — keeps source chips from shifting when results refresh. */
    const showHeaderLoadingIcon = isResolving || (hasRows && (isStale || isFetching));

    const mentionList = (
      // biome-ignore lint/a11y/useAriaPropsSupportedByRole: role is listbox or tree; both accept aria-label on the list container.
      <div
        aria-busy={isLoading}
        aria-disabled={isStale && items.length > 0}
        aria-label={`${activeSource.label} mention suggestions`}
        className={cn('flex shrink-0 flex-col gap-1', isStale && items.length > 0 && 'pointer-events-none opacity-60')}
        data-mention-pending={String(isStale || isFetching)}
        data-testid="WORKSPACE_AGENT_MENTION_LIST"
        id={WORKSPACE_AGENT_MENTION_LIST_ID}
        role={activeSource.listRole}
        tabIndex={-1}
      >
        {leadingItems.map((item, index) => renderRowWithSentinels(item, index))}
        {libraryRunItems.length > 0 ? (
          <>
            <div
              aria-hidden
              className="px-1 py-2 font-bold ads:text-metadata text-10 text-gray-10 uppercase"
              data-testid="WORKSPACE_AGENT_MENTION_LIBRARIES_LABEL"
            >
              {WORKSPACE_AGENT_MENTION_LIBRARIES_LABEL}
            </div>
            <fieldset
              aria-label={WORKSPACE_AGENT_MENTION_LIBRARIES_LABEL}
              className="flex flex-col gap-1"
              data-testid="WORKSPACE_AGENT_MENTION_LIBRARIES_GROUP"
            >
              {libraryRunItems.map((item, index) => renderRowWithSentinels(item, firstLibraryIndex + index))}
            </fieldset>
          </>
        ) : null}
      </div>
    );

    return (
      <>
        <RadixPopover.Root open>
          {/* `virtualRef` takes any `getBoundingClientRect` — here the composer's own element. */}
          <RadixPopover.Anchor virtualRef={anchorRef} />
          <RadixPopover.Portal container={portal}>
            <RadixPopover.Content
              align="start"
              /**
               * A fixed box, not one that grows to its rows: the chips row is the only thing anchored
               * to the caret, so a panel that resized per keystroke would walk it up and down the
               * screen while the user is still typing. Every state — rows, spinner, empty, error —
               * renders inside this same height.
               */
              className={popoverStyles.content()}
              collisionPadding={8}
              data-testid="WORKSPACE_AGENT_MENTION_POPUP"
              onEscapeKeyDown={handleEscapeKeyDown}
              onOpenAutoFocus={handleOpenAutoFocus}
              onPointerDownOutside={handlePointerDownOutside}
              // Radix hardcodes `dialog`, which would announce the composer's own popup as a modal.
              role="presentation"
              side={side}
              sideOffset={resolvedSideOffset}
              style={{ height: WORKSPACE_AGENT_COMPOSER_REFERENCE_MENU_POPOVER_HEIGHT_PX }}
            >
              <div className={headerStyles.row()} data-testid="WORKSPACE_AGENT_MENTION_HEADER">
                <div aria-hidden className={headerStyles.titleCluster()}>
                  <span
                    className={headerStyles.iconWrap()}
                    data-loading={String(showHeaderLoadingIcon)}
                    data-testid="WORKSPACE_AGENT_MENTION_HEADER_ICON"
                  >
                    {showHeaderLoadingIcon ? (
                      <Spinner aria-hidden className="size-4 ads:text-icon-subtle text-gray-9" />
                    ) : (
                      <AtSignIcon aria-hidden className="ads:text-icon-emphasis text-gray-12" size="sm" />
                    )}
                  </span>
                  <span className={headerStyles.title()} data-testid="WORKSPACE_AGENT_MENTION_TITLE">
                    {WORKSPACE_AGENT_REFERENCE_MENU_TITLE}
                  </span>
                </div>
                <fieldset aria-label="Mention source" className={headerStyles.chips()}>
                  {sources.map((source) => (
                    <WorkspaceAgentMentionFilterChip
                      isActive={source.key === activeSourceKey}
                      key={source.key}
                      label={source.label}
                      onSelect={onSourceChange}
                      sourceKey={source.key}
                    />
                  ))}
                </fieldset>
              </div>
              {isContextsSource ? (
                <WorkspaceAgentMentionContextsBody
                  brandKits={brandKits}
                  emptyMessage={emptyStateTitle}
                  hasFailed={hasFailed}
                  hasRows={hasRows}
                  isKitMenuOpen={isContextsKitMenuOpen}
                  kitMenuTriggerRef={contextsKitTriggerRef}
                  listRef={listRef}
                  onDismiss={onDismiss}
                  onKitMenuOpenChange={setIsContextsKitMenuOpen}
                  onKitMenuOpening={handleContextsKitMenuOpening}
                  onListMouseLeave={closeRowPreview}
                  onListMouseMove={handleListMouseMove}
                  onRefocusEditor={onRefocusEditor}
                  onRetry={onRetry}
                  onSelectBrandKit={handleSelectBrandKit}
                  selectedBrandKitId={selectedBrandKitId}
                  showEmpty={showEmptyState}
                >
                  <div style={WORKSPACE_AGENT_MENTION_LIST_RING_CLEARANCE_STYLE}>{mentionList}</div>
                  {hasNextPage ? (
                    <WorkspaceAgentMentionListSentinel
                      isFetching={isFetching}
                      listRef={listRef}
                      onFetch={handleFetchNextPage}
                      rootMargin={MENTION_SENTINEL_ROOT_MARGIN}
                    />
                  ) : null}
                </WorkspaceAgentMentionContextsBody>
              ) : (
                /* biome-ignore lint/a11y/noStaticElementInteractions: the move only reads which input is driving; nothing here is operable */
                <div
                  className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2"
                  data-testid="WORKSPACE_AGENT_MENTION_LIST_SCROLLER"
                  onMouseLeave={closeRowPreview}
                  onMouseMove={handleListMouseMove}
                  ref={listRef}
                >
                  <div style={WORKSPACE_AGENT_MENTION_LIST_RING_CLEARANCE_STYLE}>{mentionList}</div>
                  {showEmptyState ? (
                    <div
                      className="flex items-center gap-2 p-2 ads:text-metadata text-12 text-gray-10"
                      data-testid="WORKSPACE_AGENT_MENTION_EMPTY_STATE"
                    >
                      <p>{emptyStateTitle}</p>
                      {hasFailed ? (
                        <Button
                          appearance="ghost"
                          color="gray"
                          data-testid="WORKSPACE_AGENT_MENTION_EMPTY_STATE_ACTION"
                          onClick={onRetry}
                          onMouseDown={handleRetryMouseDown}
                          size="small"
                          tabIndex={-1}
                        >
                          Retry
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                  {boardListQuerySpecs.map((spec) => (
                    <PrivateWorkspaceAgentBoardListQuery
                      enabled
                      key={spec.nodeId}
                      onSnapshot={onListSnapshot}
                      spec={spec}
                    />
                  ))}
                  {hasNextPage ? (
                    <WorkspaceAgentMentionListSentinel
                      isFetching={isFetching}
                      listRef={listRef}
                      onFetch={handleFetchNextPage}
                      rootMargin={MENTION_SENTINEL_ROOT_MARGIN}
                    />
                  ) : null}
                </div>
              )}
              {/* One live region for the popup's whole life: a region mounted already populated isn't announced. */}
              <div aria-live="polite" className="sr-only" data-testid="WORKSPACE_AGENT_MENTION_LIVE_REGION">
                {liveMessage}
              </div>
            </RadixPopover.Content>
          </RadixPopover.Portal>
        </RadixPopover.Root>
        {/*
         * A sibling of the picker, not a child of its content: the preview is a layer of its own,
         * above it, which is what keeps the picker's own `onEscapeKeyDown` from firing while it is
         * painted. That press is wired on the preview Content so it still dismisses the picker.
         */}
        {previewItem?.thumbnailUrl ? (
          <WorkspaceAgentMentionImageRowPreview
            anchorScopeKey={previewScopeKey}
            getPreviewAnchorElement={getMentionPreviewAnchorElement}
            /* A kit value is an image, so only an asset row can be timed media. */
            hasPlayOverlay={previewItem.kind === 'asset' && previewItem.assetType === ClipType.video}
            isArmed={isRowPreviewArmed}
            isOpen={isRowPreviewOpen}
            onEscapeKeyDown={handleEscapeKeyDown}
            thumbnailUrl={previewItem.thumbnailUrl}
          />
        ) : null}
      </>
    );
  }),
);

WorkspaceAgentMentionPopup.displayName = 'WorkspaceAgentMentionPopup';
